import { useCallback, useEffect, useState } from "react";
import { calculateExponentialBackoff, calculateRemainingCooldown } from "utils/exponentialBackoff";
import type { StoredCooldown } from "./useExponentialCooldown.types";
import type {
  UseExponentialCooldownOptions,
  UseExponentialCooldownResult,
} from "./useExponentialCooldown.types";

const DEFAULT_BASE_SECONDS = 5;
const DEFAULT_MAX_COOLDOWN_SECONDS = 300;

function loadStoredCooldown(storageKey: string): StoredCooldown | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredCooldown;
    if (typeof parsed?.attempt !== "number" || typeof parsed?.lastTimestamp !== "number") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveStoredCooldown(storageKey: string, attempt: number): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ attempt, lastTimestamp: Date.now() }));
  } catch {
    // ignore
  }
}

function clearStoredCooldown(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

export const useExponentialCooldown = (
  options: UseExponentialCooldownOptions = {}
): UseExponentialCooldownResult => {
  const {
    baseSeconds = DEFAULT_BASE_SECONDS,
    maxCooldownSeconds = DEFAULT_MAX_COOLDOWN_SECONDS,
    storageKey = null,
  } = options;

  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!storageKey) return;
    const stored = loadStoredCooldown(storageKey);
    if (!stored) return;
    const remaining = calculateRemainingCooldown(stored.attempt, baseSeconds, stored.lastTimestamp);
    setAttempt(stored.attempt);
    if (remaining > 0) {
      setCooldownRemaining(remaining);
    }
  }, [storageKey, baseSeconds]);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setTimeout(() => setCooldownRemaining((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownRemaining]);

  const triggerCooldown = useCallback(() => {
    const nextAttempt = attempt + 1;
    const cooldownSeconds = Math.min(
      calculateExponentialBackoff(nextAttempt, baseSeconds),
      maxCooldownSeconds
    );
    setAttempt(nextAttempt);
    setCooldownRemaining(cooldownSeconds);
    if (storageKey) {
      saveStoredCooldown(storageKey, nextAttempt);
    }
  }, [attempt, baseSeconds, maxCooldownSeconds, storageKey]);

  const resetCooldown = useCallback(() => {
    setAttempt(0);
    setCooldownRemaining(0);
    if (storageKey) {
      clearStoredCooldown(storageKey);
    }
  }, [storageKey]);

  return {
    cooldownRemaining,
    isOnCooldown: cooldownRemaining > 0,
    attempt,
    triggerCooldown,
    resetCooldown,
  };
};
